import React, { useMemo } from "react";
import { ChefHat, User } from "lucide-react";
import { OrderTypeBadge } from "../../../components/OrderTypeBadge";
import type { FornoItem } from "../../../services/fornoService";
import { TemposEtapasBadges } from "./TemposEtapasBadges";
import { segundosEntreInstantes } from "../utils/temposPainel";
import {
  resolvePainelCardUrgencia,
  resolveSegundosNoForno,
} from "../utils/painelCardUrgencia";
import { formatPainelDescricaoResumo } from "../utils/descricaoPainel";
import { PainelPedidoOriginalButton } from "./PainelPedidoOriginalButton";
import { PainelCardActionRow } from "./PainelCardActionRow";
import {
  PainelCardAmberNote,
  PainelCardDetailRow,
  PainelCardShell,
  PainelCardTemposSection,
} from "./PainelCardShell";
import { resolveFornoItemObservation } from "../../../utils/fornoItemUtils";
import {
  isPedidoSalaoFoodfly,
  resolveSalaoProducaoLabels,
} from "../utils/origemPainel";
import SalaoProducaoOrderHeader from "../../../components/SalaoProducaoOrderHeader";

interface CardFornoProps {
  item: FornoItem;
  nowMs: number;
  onVerPedidoOriginal?: (orderId: number) => void;
}

export const CardForno: React.FC<CardFornoProps> = ({
  item,
  nowMs,
  onVerPedidoOriginal,
}) => {
  const emFornoSeg = useMemo(
    () => resolveSegundosNoForno(item, nowMs),
    [item, nowMs],
  );
  const urgencia = resolvePainelCardUrgencia(emFornoSeg);
  const temInicioForno = emFornoSeg != null;

  let aguardandoMontagemSeg: number | null = null;
  if (item.filaCreatedAt && item.montagemStartedAt) {
    aguardandoMontagemSeg = segundosEntreInstantes(
      item.filaCreatedAt,
      item.montagemStartedAt,
    );
  }

  const isSalao = isPedidoSalaoFoodfly({ origem: item.origem });
  const salaoLabels = isSalao
    ? resolveSalaoProducaoLabels({
        mesaId: item.salaoMesaId,
        comandaId: item.salaoComandaId,
        identificador: item.salaoIdentificador,
        displayId: item.displayId,
      })
    : null;
  const observation = resolveFornoItemObservation(item);
  const customerWeb = !isSalao
    ? item.pedidoIntegracao?.customerName ?? null
    : null;

  const headerEnd = !isSalao ? (
    <OrderTypeBadge
      orderType={item.pedidoIntegracao?.orderType}
      iconSize={14}
      withTooltip={false}
    />
  ) : undefined;

  return (
    <PainelCardShell
      displayId={item.displayId}
      isSalao={isSalao}
      headerBadges={
        isSalao ? (
          <SalaoProducaoOrderHeader
            labels={salaoLabels}
            showSalaoTag
            layout="stacked"
          />
        ) : undefined
      }
      badgeLabel={!isSalao ? `Pedido #${item.displayId}` : undefined}
      urgencia={urgencia}
      headerEnd={headerEnd}
    >
      <p className="mb-4 line-clamp-4 text-sm font-semibold leading-relaxed text-neutral-800 [[data-salao-card]_&]:text-white">
        {formatPainelDescricaoResumo(item.descricao)}
      </p>

      <div>
        {!isSalao && customerWeb && (
          <PainelCardDetailRow icon={User}>{customerWeb}</PainelCardDetailRow>
        )}
        {item.responsavelNome && (
          <PainelCardDetailRow icon={ChefHat}>
            {item.responsavelNome}
          </PainelCardDetailRow>
        )}
      </div>

      {observation && <PainelCardAmberNote>{observation}</PainelCardAmberNote>}

      <PainelCardTemposSection>
        <TemposEtapasBadges
          displayId={item.displayId}
          hidePedidoId={
            !!(salaoLabels?.mesa || salaoLabels?.comandaLabel)
          }
          aguardandoMontagemSeg={aguardandoMontagemSeg}
          emMontagemSeg={item.tempoMontagem ?? null}
          emFornoSeg={temInicioForno ? emFornoSeg : null}
          aguardandoEntregaSeg={null}
          emEntregaSeg={null}
        />
      </PainelCardTemposSection>

      {onVerPedidoOriginal && item.orderId > 0 && (
        <PainelCardActionRow>
          <PainelPedidoOriginalButton
            orderId={item.orderId}
            onOpen={onVerPedidoOriginal}
          />
        </PainelCardActionRow>
      )}
    </PainelCardShell>
  );
};
